import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, PackageOpen, PackageCheck, Boxes, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OrderStatusPill, StockTable, STOCK_REASONS, when, ugx } from "@/components/supply/shared";
import { StockAdjustDialog, ReconcileForm } from "@/components/supply/StockForms";

const OrderCard = ({ o, i, action, label, icon: Icon, onAct, busy }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between gap-3" data-testid={`station-order-${action}-${i}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2"><span className="font-mono text-xs text-slate-800">{o.number}</span><OrderStatusPill status={o.status} /></div>
      <div className="text-sm text-slate-900 mt-1 truncate">{o.household_name} · {o.cylinder_type}</div>
      <div className="text-[11px] text-slate-500">{o.rider_name ? `Rider: ${o.rider_name}` : "No rider yet"} · {when(o.created_at)}</div>
    </div>
    <Button size="sm" disabled={busy === o.id} className="rounded-sm bg-brand-600 hover:bg-brand-700 shrink-0" onClick={() => onAct(o, action)} data-testid={`station-${action}-btn-${i}`}><Icon size={14} className="mr-1" />{label}</Button>
  </div>
);

const Section = ({ title, empty, children }) => (
  <div className="space-y-2">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{title}</div>
    {React.Children.count(children) ? children : <div className="text-xs text-slate-400 bg-white border border-dashed border-slate-200 rounded-sm p-4 text-center">{empty}</div>}
  </div>
);

export default function StationApp() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [station, setStation] = useState(null);
  const [orders, setOrders] = useState([]);
  const [moves, setMoves] = useState([]);
  const [adjust, setAdjust] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([api.get("/stations"), api.get("/refill-orders", { params: { status: "REQUESTED,RELEASED,PICKED_UP,DELIVERED" } })]);
      const st = (s.data.data || [])[0] || null;
      setStation(st); setOrders(o.data.data || []);
      if (st) { const m = await api.get(`/stations/${st.id}/stock-moves`, { params: { limit: 30 } }); setMoves(m.data.data || []); }
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Failed to load station"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [busy, setBusy] = useState(null);
  const act = async (o, action) => {
    if (busy) return;
    setBusy(o.id);
    try { await api.post(`/refill-orders/${o.id}/${action}`, {}); toast.success(action === "release" ? `${o.number} released` : `Empty for ${o.number} accepted`); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };
  const toRelease = orders.filter((o) => o.status === "REQUESTED");
  const empties = orders.filter((o) => ["PICKED_UP", "DELIVERED"].includes(o.status));
  const released = orders.filter((o) => o.status === "RELEASED");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="station-app">
      <header className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img src="/gasify_logo.png" alt="Gasify" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.25em] text-flame-400 font-semibold">Station</div>
            <div className="text-xs text-slate-300" data-testid="station-app-name">{station?.name || "No station linked"} · {user?.name}</div>
          </div>
        </div>
        <button onClick={async () => { await logout(); nav("/login"); }} className="text-slate-400 hover:text-white p-2" aria-label="Sign out" data-testid="station-logout-btn"><LogOut size={18} /></button>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5">
        {!station ? <div className="text-sm text-slate-500">Your account isn't linked to a station yet — ask a Gasify admin.</div> : (
          <Tabs defaultValue="orders">
            <TabsList className="rounded-sm w-full grid grid-cols-3">
              <TabsTrigger value="orders" data-testid="station-tab-orders"><PackageOpen size={14} className="mr-1" />Orders</TabsTrigger>
              <TabsTrigger value="stock" data-testid="station-tab-stock"><Boxes size={14} className="mr-1" />Stock</TabsTrigger>
              <TabsTrigger value="eod" data-testid="station-tab-eod"><ClipboardCheck size={14} className="mr-1" />End of day</TabsTrigger>
            </TabsList>
            <TabsContent value="orders" className="mt-4 space-y-6">
              <Section title={`To release (${toRelease.length})`} empty="No refills waiting.">
                {toRelease.map((o, i) => <OrderCard key={o.id} o={o} i={i} action="release" label="Release" icon={PackageOpen} onAct={act} busy={busy} />)}
              </Section>
              <Section title={`Empties to accept (${empties.length})`} empty="No empties due back.">
                {empties.map((o, i) => <OrderCard key={o.id} o={o} i={i} action="return-empty" label="Accept empty" icon={PackageCheck} onAct={act} busy={busy} />)}
              </Section>
              <Section title={`Awaiting rider pickup (${released.length})`} empty="Nothing waiting for pickup.">
                {released.map((o) => <div key={o.id} className="text-xs bg-white border border-slate-200 rounded-sm px-3 py-2">{o.number} · {o.household_name} · {o.cylinder_type} · {ugx(o.refill_cost)}</div>)}
              </Section>
            </TabsContent>
            <TabsContent value="stock" className="mt-4 space-y-4">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <StockTable rows={station.stock_rows} testId="station-app-stock" />
                <Button className="w-full mt-4 rounded-sm bg-brand-600 hover:bg-brand-700" onClick={() => setAdjust(true)} data-testid="station-stock-adjust-btn">Record stock movement</Button>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="station-stock-moves">
                {moves.map((m) => (
                  <div key={m.id} className="px-4 py-2 text-xs flex justify-between gap-2">
                    <div><div className="text-slate-800">{STOCK_REASONS[m.reason] || m.reason}</div><div className="text-slate-400">{when(m.at)} · {m.by?.name}</div></div>
                    <div className="tabular-nums text-right text-slate-600">full {m.full_delta >= 0 ? "+" : ""}{m.full_delta}<br />empty {m.empty_delta >= 0 ? "+" : ""}{m.empty_delta}</div>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="eod" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-sm p-4"><ReconcileForm station={station} onDone={load} /></div>
            </TabsContent>
          </Tabs>
        )}
      </main>
      <StockAdjustDialog station={station} open={adjust} onOpenChange={setAdjust} onSaved={load} />
    </div>
  );
}
